// Shared slot classification + label logic for the customer booking surfaces.
//
// Ported verbatim from EventSelectionList.vue so the Calendar view renders slot states
// and custom text identically to the list and card views. Keep this the single source
// of truth: when slot behavior changes, change it here. (EventSelectionList still keeps
// its own inline copy for now; migrating it onto this util is a safe follow-up.)
//
// All functions are pure: they take (game, slot, bss, now) explicitly. Display/i18n
// formatting (time strings, $t fallbacks) stays in the component. When now is
// omitted it defaults to getNow(), the backend-synced venue wall clock, so the
// device's clock/timezone can never shift which slots appear bookable.

import { getNow } from '@/util/serverTime'

export function isPublicBooking(game, slot) {
  if (slot.is_custom_pricing_enabled) {
    return slot.is_public_booking
  }
  return game.is_public_booking
}

export function hasRemainingSlots(game, slot) {
  const playerHeadroom = game.max_players_count - slot.group_size
  if (slot.resource_max_remaining_seats != null) {
    return Math.min(playerHeadroom, slot.resource_max_remaining_seats) > 0
  }
  return playerHeadroom > 0
}

export function getRemainingTickets(game, slot) {
  const playerHeadroom = game.max_players_count - slot.group_size
  if (slot.resource_max_remaining_seats != null) {
    return Math.min(playerHeadroom, slot.resource_max_remaining_seats)
  }
  return playerHeadroom
}

// Resolves the min-booking-period offset (in days/minutes) onto unblock_date.
function applyMinBookingPeriod(slot, baseDate, unblockDate) {
  if (slot.min_booking_period === 0) {
    unblockDate.setMinutes(unblockDate.getMinutes() + slot.min_booking_value)
  } else if (slot.min_booking_period === 1) {
    unblockDate.setMinutes(unblockDate.getMinutes() + parseInt(slot.min_booking_value) * 60)
  } else if (slot.min_booking_period === 2) {
    unblockDate.setDate(unblockDate.getDate() + slot.min_booking_value)
  } else if (slot.min_booking_period === 3) {
    unblockDate.setDate(unblockDate.getDate() + 7 * parseInt(slot.min_booking_value))
  } else if (slot.min_booking_period === 4) {
    const futureDate = new Date(baseDate.getTime())
    futureDate.setMonth(futureDate.getMonth() + parseInt(slot.min_booking_value))
    const blockedDays = Math.ceil((futureDate.getTime() - baseDate.getTime()) / (1000 * 60 * 60 * 24))
    unblockDate.setDate(unblockDate.getDate() + blockedDays)
  }
}

export function shouldSlotBeCallToBook(slot, now) {
  now = now || getNow()
  const slotDate = new Date()
  // Start from the 1st: the date begins as today, and setting the month while on
  // the 29th-31st overflows into the following month when the target month is shorter
  slotDate.setDate(1)
  slotDate.setFullYear(slot.booking_date.split('-')[0])
  slotDate.setMonth(parseInt(slot.booking_date.split('-')[1]) - 1)
  slotDate.setDate(slot.booking_date.split('-')[2])
  slotDate.setHours(slot.start_time.split(':')[0])
  slotDate.setMinutes(slot.start_time.split(':')[1])

  let unblockDate = new Date(now.getTime())

  if (slot.allow_late_booking === 1) {
    unblockDate = new Date(slotDate.getTime())
    if (slot.min_booking_period === null) {
      unblockDate = new Date(slotDate.getTime())
    } else {
      applyMinBookingPeriod(slot, slotDate, unblockDate)
    }
    if (now >= slotDate) {
      return now > unblockDate
    }
    return false
  }

  if (slot.min_booking_period === null) {
    unblockDate = new Date(now.getTime())
  } else {
    applyMinBookingPeriod(slot, now, unblockDate)
  }
  return slotDate <= unblockDate
}

// Parses game.slots (JSON string), tags each slot with .block / .remove per the
// min/max booking window, drops out-of-window slots, and returns them ordered by time.
export function normalizeSlots(game, bss, now) {
  return normalizeSlotArray(JSON.parse(game.slots), game, bss, now)
}

// Same as normalizeSlots but takes an already-parsed slot array (e.g. the month range
// fetched from games/{id}/bookings). booking_date may be ISO; callers should strip any
// time component to YYYY-MM-DD before passing it in.
export function normalizeSlotArray(slots, game, bss, now) {
  now = now || getNow()
  let s = []

  if (bss.show_unavailable_slots === 0) {
    for (const n in slots) {
      if (slots[n].online_display_mode === 'hidden') continue
      if (slots[n].online_display_mode === 'bookable' || slots[n].online_display_mode === 'non_bookable') {
        s.push(slots[n])
        continue
      }
      // Claimed by a pending request: hides exactly as a booked private slot.
      if (slots[n].status === 'requested') continue
      // Requestable with the template toggle off, or on a public master
      // (requests are private-only): hides exactly as blocked.
      if (slots[n].status === 'requestable'
        && (Number(bss.allow_request_to_book) !== 1 || isPublicBooking(game, slots[n]))) continue
      if (isPublicBooking(game, slots[n])
        || (slots[n].status !== 'booked' && slots[n].status !== 'blocked')) {
        s.push(slots[n])
      }
    }
  } else {
    s = (slots || []).filter(slot => slot.online_display_mode !== 'hidden')
  }

  if (s === null) s = []

  for (const n in s) {
    const slotDate = new Date()
    // Start from the 1st: the date begins as today, and setting the month while on
    // the 29th-31st overflows into the following month when the target month is shorter
    slotDate.setDate(1)
    slotDate.setFullYear(s[n].booking_date.split('-')[0])
    slotDate.setMonth(parseInt(s[n].booking_date.split('-')[1]) - 1)
    slotDate.setDate(s[n].booking_date.split('-')[2])
    slotDate.setHours(s[n].start_time.split(':')[0])
    slotDate.setMinutes(s[n].start_time.split(':')[1])
    let unblockDate = new Date(now.getTime())

    // Min booking period -> sets .block (call-to-book gate)
    if (s[n].allow_late_booking === 1) {
      unblockDate = new Date(slotDate.getTime())
      if (s[n].min_booking_period === null) {
        unblockDate = new Date(slotDate.getTime())
      } else {
        applyMinBookingPeriod(s[n], slotDate, unblockDate)
      }
    } else {
      if (s[n].min_booking_period === null) {
        unblockDate = new Date(now.getTime())
      } else {
        applyMinBookingPeriod(s[n], now, unblockDate)
      }
    }

    if (s[n].allow_late_booking === 1) {
      s[n].block = now > unblockDate
    } else {
      s[n].block = slotDate <= unblockDate
    }

    // Max booking period -> sets .remove (too far in the future)
    let maxDays = 0
    if (s[n].max_booking_period === 0) {
      maxDays = parseInt(s[n].max_booking_value)
    } else if (s[n].max_booking_period === 1) {
      maxDays = parseInt(s[n].max_booking_value) * 7
    } else if (s[n].max_booking_period === 2) {
      const future = new Date(now.getTime())
      const today = new Date(now.getTime())
      future.setMonth(future.getMonth() + parseInt(s[n].max_booking_value))
      maxDays = Math.ceil(Math.abs(future - today) / (1000 * 60 * 60 * 24))
    } else if (s[n].max_booking_period === 3) {
      const future = new Date(now.getTime())
      const today = new Date(now.getTime())
      future.setFullYear(future.getFullYear() + parseInt(s[n].max_booking_value))
      maxDays = Math.ceil(Math.abs(future - today) / (1000 * 60 * 60 * 24))
    } else if (s[n].max_booking_period === null) {
      maxDays = 10
    }

    const maxDate = new Date(now.getTime())
    maxDate.setDate(maxDate.getDate() + maxDays)
    s[n].remove = slotDate >= maxDate

    // When the slot has crossed its minimum-notice (or late-booking) window and the
    // schedule opted to hide rather than call-to-book, drop it from the page entirely.
    if (s[n].block && Number(s[n].hide_past_min_notice) === 1) {
      s[n].remove = true
    }
  }

  const visibleSlots = s.filter(slot => !slot.remove)
  return visibleSlots.sort((a, b) => (a.start_time < b.start_time ? -1 : a.start_time > b.start_time ? 1 : 0))
}

// Classifies a single slot into a render type. Mirror of EventSelectionList.getSlotType.
export function getSlotType(game, bss, s, now) {
  now = now || getNow()

  if (s.is_resource_saturated) return 'sold-out'

  // A custom status shown online as Sold out renders exactly as a sold out
  // slot: site sold out text and colors, no per-slot text or link.
  if (s.online_display_mode === 'non_bookable') return 'sold-out'

  // Claimed by a pending booking request: other viewers see it exactly as a
  // booked private slot.
  if (s.status === 'requested') return 'sold-out'

  // Request-to-book classification (mirrors make-booking-v2's server-side
  // derivation). A requestable slot offers a request button until its minimum
  // notice passes, then follows its own three-way past-notice behavior: call
  // to book by default, request when request_past_min_notice is set, hidden
  // when hide_past_min_notice is set (applied upstream in normalizeSlotArray).
  // An available slot books instantly until its notice passes, then follows
  // the same behavior. A closed late-booking window is never requestable.
  const allowRtb = Number(bss && bss.allow_request_to_book) === 1
  if (s.status === 'requestable') {
    // Requests are private-slot only (an approval claims the whole slot);
    // a public master marked requestable is not sellable and renders blocked.
    if (!allowRtb || isPublicBooking(game, s)) return 'blocked'
    if (!s.remove && !s.block) return 'requestable'
    if (!s.remove && s.block && s.allow_late_booking !== 1 && Number(s.request_past_min_notice) === 1) return 'requestable'
    if (s.block && !s.has_external_link) return 'call-to-book'
    return 'blocked'
  }
  if (allowRtb && s.status === 'available' && s.block && !s.remove
    && s.allow_late_booking !== 1 && Number(s.request_past_min_notice) === 1
    && !s.has_external_link && !isPublicBooking(game, s)) {
    return 'requestable'
  }
  if (s.online_display_mode === 'bookable' && s.status !== 'booked' && !s.block && !s.remove) {
    return isPublicBooking(game, s) ? 'available-public' : 'available'
  }
  if ((!s.block && !s.remove) && s.status === 'available' && !isPublicBooking(game, s)) {
    return 'available'
  }
  if ((!s.block && !s.remove) && s.status === 'available' && isPublicBooking(game, s)) {
    return 'available-public'
  }
  if (s.status === 'booked' && (s.is_reservation_upgraded || !isPublicBooking(game, s) || (isPublicBooking(game, s) && !hasRemainingSlots(game, s)))) {
    return 'sold-out'
  }
  if (s.status === 'booked' && !s.is_reservation_upgraded && isPublicBooking(game, s) && hasRemainingSlots(game, s) && shouldSlotBeCallToBook(s, now)) {
    return 'call-to-book'
  }
  if (s.status === 'booked' && !s.is_reservation_upgraded && isPublicBooking(game, s) && hasRemainingSlots(game, s) && !shouldSlotBeCallToBook(s, now)) {
    return 'available-public'
  }
  if (s.status === 'blocked') return 'blocked'
  if ((s.status === 'call_to_book' || s.block) && s.has_external_link && !(s.status === 'booked' || s.status === 'blocked')) {
    return 'call-to-book-link'
  }
  if (((s.status === 'call_to_book' && !s.remove) || (s.block && !s.remove)) && !s.has_external_link && s.slot_text && !(s.status === 'booked' || s.status === 'blocked')) {
    return 'call-to-book'
  }
  if ((s.status === 'call_to_book' || s.block) && !s.has_external_link && !s.slot_text && !(s.status === 'booked' || s.status === 'blocked')) {
    return 'call-to-book'
  }
  return 'unknown'
}

const BOOKABLE_TYPES = ['available', 'available-public', 'requestable']
const DISABLED_TYPES = ['sold-out', 'blocked', 'call-to-book']

export function isSlotBookable(type) {
  return BOOKABLE_TYPES.indexOf(type) !== -1
}

export function isSlotDisabled(type) {
  return DISABLED_TYPES.indexOf(type) !== -1
}

export function getSlotLabel(bss, type, s) {
  // Deliberately NOT s.slot_text — that carries call-to-book wording.
  if (type === 'requestable') return bss.request_to_book_text || 'Request to book'
  if (type === 'sold-out') return bss.booked_text || ''
  if (type === 'blocked') return bss.blocked_text || ''
  if (type === 'call-to-book' || type === 'call-to-book-link') {
    return s.slot_text || bss.call_to_book_text || ''
  }
  return ''
}

export function shouldShowSlotBadge(bss, type, s) {
  if (!s.custom_message || !s.custom_message_active || s.custom_message_display !== 'badge') return false
  if (bss.hide_custom_message_when_sold_out && type === 'sold-out') return false
  return true
}

export function shouldUseCustomText(bss, type, s) {
  if (!s.custom_message || !s.custom_message_active || s.custom_message_display !== 'text') return false
  if (bss.hide_custom_message_when_sold_out && type === 'sold-out') return false
  return true
}

export function showTicketCount(bss, type) {
  return bss.show_available_ticket_count && type === 'available-public'
}
